import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Video, ExternalLink, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Zero-setup Teams meeting scheduling via deep-link.
 * The browser opens Teams (desktop app if installed, else web) with the meeting pre-filled;
 * the user confirms with "Send" inside Teams. We also log a "meeting" activity on the lead.
 */

// Default = tomorrow at 10:00
const defaultDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
};

const buildTeamsDeeplink = ({ subject, startISO, endISO, content, attendees }) => {
  const params = new URLSearchParams();
  if (subject) params.set("subject", subject);
  if (startISO) params.set("startTime", startISO);
  if (endISO) params.set("endTime", endISO);
  if (content) params.set("content", content);
  if (attendees) params.set("attendees", attendees);
  return `https://teams.microsoft.com/l/meeting/new?${params.toString()}`;
};

export default function ScheduleTeamsMeetingDialog({ lead, open, onOpenChange, onScheduled, activity = null }) {
  const isEdit = Boolean(activity?.id);
  const [subject, setSubject] = useState("");
  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState("30");
  const [agenda, setAgenda] = useState("");
  const [attendees, setAttendees] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset form when opening — prefill from `activity` in edit mode, or from lead defaults on create.
  useEffect(() => {
    if (!open || !lead) return;
    if (isEdit) {
      const meta = activity?.meta || {};
      setSubject(meta.subject || `Meeting with ${lead.name}${lead.company ? ` — ${lead.company}` : ""}`);
      setAttendees(meta.attendees || lead.email || "");
      // agenda not stored separately — leave blank, user can add fresh notes
      setAgenda("");
      if (meta.start) {
        const s = new Date(meta.start);
        if (!Number.isNaN(s.getTime())) {
          const pad = (n) => String(n).padStart(2, "0");
          setDate(`${s.getFullYear()}-${pad(s.getMonth() + 1)}-${pad(s.getDate())}`);
          setTime(`${pad(s.getHours())}:${pad(s.getMinutes())}`);
        }
      }
      if (meta.start && meta.end) {
        const diff = Math.round((new Date(meta.end).getTime() - new Date(meta.start).getTime()) / 60000);
        if (diff > 0) setDuration(String(diff));
      }
    } else {
      setSubject(`Meeting with ${lead.name}${lead.company ? ` — ${lead.company}` : ""}`);
      setAttendees(lead.email || "");
      setAgenda("");
      setDate(defaultDate());
      setTime("10:00");
      setDuration("30");
    }
  }, [open, lead?.id, activity?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { startISO, endISO, prettyRange } = useMemo(() => {
    if (!date || !time) return { startISO: "", endISO: "", prettyRange: "" };
    const start = new Date(`${date}T${time}`);
    if (Number.isNaN(start.getTime())) return { startISO: "", endISO: "", prettyRange: "" };
    const end = new Date(start.getTime() + Number(duration || 30) * 60000);
    const fmt = (d) => d.toLocaleString(undefined, {
      weekday: "short", day: "numeric", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
    const fmtTime = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return {
      startISO: start.toISOString(),
      endISO: end.toISOString(),
      prettyRange: `${fmt(start)} → ${fmtTime(end)}`,
    };
  }, [date, time, duration]);

  if (!lead) return null;

  const submit = async () => {
    if (!subject.trim()) return toast.error("Subject is required");
    if (!startISO || !endISO) return toast.error("Pick a valid date and time");

    setBusy(true);
    try {
      const url = buildTeamsDeeplink({
        subject: subject.trim(),
        startISO,
        endISO,
        content: agenda.trim() || `Scheduled from CERF LeadFlow — lead: ${lead.name}`,
        attendees: attendees.trim(),
      });

      const meta = {
        start: startISO,
        end: endISO,
        subject: subject.trim(),
        attendees: attendees.trim(),
        teams_url: url,
        provider: "teams",
      };
      const line = `Teams meeting ${isEdit ? "rescheduled" : "scheduled"} for ${prettyRange}`
        + (attendees ? ` with ${attendees}` : "")
        + (agenda ? `\nAgenda: ${agenda}` : "");
      try {
        if (isEdit) {
          await api.patch(`/activities/${activity.id}`, { content: line, meta });
        } else {
          await api.post(`/leads/${lead.id}/activities`, { type: "meeting", content: line, meta });
        }
      } catch (e) {
        console.warn("[Teams] activity save failed", e); // eslint-disable-line no-console
      }

      // Open Teams in a new tab so the user can send the (updated) invite
      const w = window.open(url, "_blank", "noopener,noreferrer");
      if (!w) {
        toast.error("Your browser blocked the popup. Allow pop-ups and try again.");
        setBusy(false);
        return;
      }
      toast.success(isEdit
        ? "Meeting rescheduled — send the updated invite from Teams."
        : "Opening Microsoft Teams — click Send inside Teams to confirm the invite.");
      onScheduled?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="teams-meeting-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <span className="w-8 h-8 rounded-lg bg-[#464EB8] flex items-center justify-center">
              <Video className="w-4 h-4 text-white" />
            </span>
            {isEdit ? "Reschedule Teams meeting" : "Schedule a Microsoft Teams meeting"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-slate-100/70 border-slate-200"
              data-testid="teams-meeting-subject"
              placeholder="Meeting with…"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bg-slate-100/70 border-slate-200"
                data-testid="teams-meeting-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bg-slate-100/70 border-slate-200"
                data-testid="teams-meeting-time"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="bg-slate-100/70 border-slate-200" data-testid="teams-meeting-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="45">45 min</SelectItem>
                  <SelectItem value="60">60 min</SelectItem>
                  <SelectItem value="90">90 min</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Attendees (comma-separated emails)</Label>
            <Input
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              className="bg-slate-100/70 border-slate-200"
              data-testid="teams-meeting-attendees"
              placeholder="lead@example.com, teammate@cerf.com"
            />
            {!lead.email && (
              <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> This lead has no email — you'll need to add attendees manually inside Teams.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">Agenda</Label>
            <Textarea
              rows={3}
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              className="bg-slate-100/70 border-slate-200"
              data-testid="teams-meeting-agenda"
              placeholder="Discovery call — confirm requirements, budget and next steps."
            />
          </div>

          {startISO && (
            <p className="text-xs text-slate-500">
              <span className="font-medium text-slate-700">When:</span> {prettyRange}
            </p>
          )}
        </div>

        <DialogFooter className="mt-3 flex flex-col-reverse sm:flex-row gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} data-testid="teams-meeting-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-[#464EB8] hover:bg-[#3C43A0] text-white"
            data-testid="teams-meeting-open"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {busy ? "Opening…" : (isEdit ? "Save & open in Teams" : "Open in Teams")}
          </Button>
        </DialogFooter>

        <p className="text-[11px] text-slate-400 leading-relaxed mt-2">
          You'll be taken to Microsoft Teams with the meeting pre-filled. Click <span className="font-medium">Send</span> inside Teams
          to confirm the invite — the meeting will land on your Teams / Outlook calendar and attendees will receive the invite email.
        </p>
      </DialogContent>
    </Dialog>
  );
}
